/**
 * Generalized, config-driven public-ArcGIS feature renderer (#213).
 *
 * ONE imperative installer renders ANY arcgis_public_features category off an
 * `ArcgisCategoryConfig` (see arcgisCategories.ts) — it replaces the bespoke #204
 * evacuation layer, which now flows through this same path. It is geometry-aware:
 *   • Polygon / MultiPolygon → a translucent AREA fill + outline (evac orders, fire
 *     bans, SoLE declarations, drought basins, cyclone cones).
 *   • Point / MultiPoint     → a coloured dot (MB drought stations, boil-water
 *     systems, cyclone forecast positions).
 *   • LineString             → a coloured track line.
 * Colour comes from the category's context-locked palette, paired with the register
 * label in the side panel.
 *
 * Like the original evac layer, MapView owns the viewer, so this is imperative:
 * MapView drives `requestRefresh` on camera moves, `applyVisibility` on a toggle, and
 * `pick` from its central click handler. A thin MANAGER (installArcgisCategoryLayers)
 * mounts one layer per config and fans those calls out, so MapView wires the whole
 * family in one place.
 *
 * Honest-degrade + ambient register (operator-cop-doctrine §3B.8): every category is
 * default OFF and gated to regional+ zoom (`REGIONAL_VISIBLE_FAR_M`) so the
 * country-zoom default view never clutters; nothing pulses.
 */
import * as Cesium from 'cesium';
import type { ArcgisFeature, ArcgisFeatureCollection } from '@tsr/shared-types';
import { fetchArcgisFeatures, type BboxQuery } from '../api';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import {
  ARCGIS_CATEGORIES,
  type ArcgisCategoryConfig,
} from './arcgisCategories';
import { renderDiscGlyph } from '../discGlyphComposite';
import { polygonCentroid } from './polygonCentroid';

const REFRESH_DEBOUNCE_MS = 500;
/** Icon one-swing #564: the centroid disc size (px) for a category that declares a
 *  `centroidGlyph` (evac orders). Fixed — an ambient arcgis polygon carries no level. */
const CENTROID_DISC_SIZE = 26;
const UUID_RE = /^[0-9a-f-]{36}$/i;

/** Resolve the feature uuid carried by a GeoJsonDataSource entity. Cesium copies the
 *  feature `properties` bag onto every sub-geometry entity (whose own entity.id is a
 *  generated GUID), so the property bag is the reliable key; entity.id is the
 *  single-geometry fallback. Pure + exported for unit tests. */
export function readArcgisId(entity: { id?: unknown; properties?: unknown }): string | null {
  const props = entity.properties as
    | { id?: { getValue?: (time?: unknown) => unknown } }
    | null
    | undefined;
  const idProp = props?.id;
  if (idProp && typeof idProp.getValue === 'function') {
    const v = idProp.getValue();
    if (typeof v === 'string' && v) return v;
  }
  if (typeof entity.id === 'string' && UUID_RE.test(entity.id)) return entity.id;
  return null;
}

export interface ArcgisFeatureLayerOptions {
  /** Current toggle state — used to set ds.show after a fetch completes. */
  getVisible?: () => boolean;
  /** Reports the rendered feature count (for the toggle label / debugging). */
  onCount?: (n: number) => void;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<ArcgisFeatureCollection>;
}

export interface ArcgisFeatureLayerHandle {
  config: ArcgisCategoryConfig;
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its arcgis feature (null if not ours). */
  pick(entity: unknown): ArcgisFeature | null;
  destroy(): void;
}

export function installArcgisFeatureLayer(
  viewer: Cesium.Viewer,
  config: ArcgisCategoryConfig,
  opts: ArcgisFeatureLayerOptions = {},
): ArcgisFeatureLayerHandle {
  const fetcher =
    opts.fetcher ?? ((bbox, signal) => fetchArcgisFeatures(bbox, signal, config.category));
  const ds = new Cesium.GeoJsonDataSource(`arcgis-${config.category}`);
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, ArcgisFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load() resolving after a newer one.
  let loadGen = 0;

  // Regional-zoom render gate: hidden at country zoom, visible regional+ (ambient
  // layers never clutter the national default view).
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const styleEntities = (fc: ArcgisFeatureCollection): void => {
    const byId = new Map<string, ArcgisFeature>();
    for (const f of fc.features) byId.set(f.id, f);
    entityToFeature.clear();
    for (const entity of ds.entities.values) {
      const id = readArcgisId(entity);
      const feature = id ? byId.get(id) : undefined;
      if (!feature) continue;
      const style = config.style(feature.properties);
      const color = Cesium.Color.fromCssColorString(style.color);
      if (entity.polygon) {
        entity.polygon.material = new Cesium.ColorMaterialProperty(color.withAlpha(style.fillAlpha));
        // height 0 (not clamp-to-ground) so the outline renders.
        entity.polygon.height = new Cesium.ConstantProperty(0);
        entity.polygon.outline = new Cesium.ConstantProperty(true);
        entity.polygon.outlineColor = new Cesium.ConstantProperty(color.withAlpha(style.strokeAlpha));
        entity.polygon.outlineWidth = new Cesium.ConstantProperty(style.outlineWidth);
        entity.polygon.distanceDisplayCondition = new Cesium.ConstantProperty(gate);
        // Icon one-swing #564: a category that declares a `centroidGlyph` (evac orders)
        // gets the EXACT-ACT DISC at the polygon centroid — an evac polygon rendered NO
        // symbol before (the founder missing-glyph gap). A solid category-coloured disc +
        // white glyph knocked out, ON the polygon entity (pickable → the same feature),
        // regional-gated to match the area. Only for categories that opt in.
        if (config.centroidGlyph) {
          const centroid = polygonCentroid(feature.geometry);
          if (centroid) {
            if (!entity.position) {
              entity.position = new Cesium.ConstantPositionProperty(
                Cesium.Cartesian3.fromDegrees(centroid[0], centroid[1]),
              );
            }
            entity.billboard = new Cesium.BillboardGraphics({
              image: renderDiscGlyph(config.centroidGlyph, { fill: style.color }) ?? undefined,
              width: CENTROID_DISC_SIZE,
              height: CENTROID_DISC_SIZE,
              color: Cesium.Color.WHITE,
              verticalOrigin: Cesium.VerticalOrigin.CENTER,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              distanceDisplayCondition: gate,
            });
          }
        }
      } else if (entity.polyline) {
        // Track line (e.g. the #217 hurricane forecast track). GROUND-CLAMPED so it
        // drapes on the surface BENEATH the (height-0) area fill of the same category
        // — z-order line < cone < dots: the position dots carry disableDepthTestDistance
        // (below) so they always draw on top and stay the click target. Matches the
        // road-closures / sectors ground-line signature.
        entity.polyline.material = new Cesium.ColorMaterialProperty(color.withAlpha(style.strokeAlpha));
        entity.polyline.width = new Cesium.ConstantProperty(style.outlineWidth);
        entity.polyline.clampToGround = new Cesium.ConstantProperty(true);
        entity.polyline.distanceDisplayCondition = new Cesium.ConstantProperty(gate);
      } else {
        // Point feed: GeoJsonDataSource defaults a pin billboard — replace it with a
        // simple coloured dot in the family colour so the register reads cleanly.
        entity.billboard = undefined;
        entity.point = new Cesium.PointGraphics({
          pixelSize: style.pointPixelSize,
          color: color.withAlpha(0.95),
          outlineColor: Cesium.Color.WHITE.withAlpha(style.strokeAlpha),
          outlineWidth: 1.5,
          distanceDisplayCondition: gate,
          // Always draw the dot over terrain (it has no real elevation).
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        });
      }
      entityToFeature.set(entity, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      await ds.load(fc, { clampToGround: false });
      if (destroyed || gen !== loadGen) return;
      styleEntities(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error(`fetchArcgisFeatures (${config.category}) failed`, err);
    }
  };

  return {
    config,
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): ArcgisFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}

// ── Manager: one layer per category, fan-out from MapView ─────────────────────

export interface ArcgisLayerHit {
  feature: ArcgisFeature;
  config: ArcgisCategoryConfig;
}

export interface ArcgisCategoryLayersOptions {
  /** Current toggle state for a category (by `config.category`). */
  getVisibility: (category: string) => boolean;
  /** Optional override of the category set (defaults to ARCGIS_CATEGORIES). */
  configs?: readonly ArcgisCategoryConfig[];
  /** Optional per-layer fetcher injection (test seam), keyed by category. */
  fetcherFor?: (
    config: ArcgisCategoryConfig,
  ) => ((bbox: BboxQuery, signal: AbortSignal) => Promise<ArcgisFeatureCollection>) | undefined;
}

export interface ArcgisCategoryLayersHandle {
  /** Debounced refresh of every CURRENTLY-VISIBLE category layer. */
  requestRefresh(bbox: BboxQuery): void;
  /** Apply each category's show state; fetch any that just became visible. */
  applyVisibility(bbox?: BboxQuery): void;
  /** Map a picked entity back to its feature + originating category config. */
  pick(entity: unknown): ArcgisLayerHit | null;
  destroy(): void;
}

export function installArcgisCategoryLayers(
  viewer: Cesium.Viewer,
  opts: ArcgisCategoryLayersOptions,
): ArcgisCategoryLayersHandle {
  const configs = opts.configs ?? ARCGIS_CATEGORIES;
  const layers = configs.map((config) =>
    installArcgisFeatureLayer(viewer, config, {
      getVisible: () => opts.getVisibility(config.category),
      fetcher: opts.fetcherFor?.(config),
    }),
  );

  return {
    requestRefresh(bbox: BboxQuery): void {
      for (const layer of layers) {
        if (opts.getVisibility(layer.config.category)) layer.requestRefresh(bbox);
      }
    },
    applyVisibility(bbox?: BboxQuery): void {
      for (const layer of layers) {
        const show = opts.getVisibility(layer.config.category);
        layer.setVisible(show);
        if (show && bbox) layer.requestRefresh(bbox);
      }
    },
    pick(entity: unknown): ArcgisLayerHit | null {
      for (const layer of layers) {
        const feature = layer.pick(entity);
        if (feature) return { feature, config: layer.config };
      }
      return null;
    },
    destroy(): void {
      for (const layer of layers) layer.destroy();
    },
  };
}
